import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router, UrlTree } from '@angular/router';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { filter, map, startWith } from 'rxjs';

type QueryParamPrimitive = string | number | boolean;
type QueryParamsInit = Readonly<Record<string, QueryParamPrimitive | null | undefined>>;

@Injectable({
  providedIn: 'root',
})
export class UrlParamsService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly isBrowser = inject(PlatformContextService).isBrowser();
  private readonly logs = inject(LogsService);

  /**
   * A reactive signal representing the query parameters of the current route.
   * It is initialized with an empty object by default and will update automatically
   * whenever the query parameters in the route change.
   *
   * The signal can be used to track and react to changes in query parameters
   * within the application.
   *
   * @type {Signal<Params>} A signal encapsulating the query parameters as an object.
   *
   * @remarks
   * This signal is created using `toSignal`, which converts the route's query parameters
   * into a reactive state. The `initialValue` option ensures that the signal starts with
   * an empty object when there are no query parameters present. When the route's query
   * parameters are updated, the signal automatically updates to reflect the new values.
   *
   * @example
   * ```ts
   * import { effect } from '@angular/core';
   *
   * // Reacting to changes in query parameters
   * effect(() => {
   *   const params = this.params();
   *   console.log('Query parameters updated:', params);
   * });
   *
   * // Accessing a specific query parameter
   * const searchTerm = this.params().search || '';
   * console.log('Search term:', searchTerm);
   * ```
   *
   * @see {@link https://angular.io/api/core/Signal} for more details on the Signal API.
   */
  readonly params: Signal<Params> = toSignal(this.route.queryParams, { initialValue: {} });

  private readonly urlSignal: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );
  private pending = new Map<string, string>();
  private flushScheduled = false;

  /**
   * Computes and returns the current URL as a reactive signal, with an option to include query parameters.
   *
   * The returned URL represents the current state of the application's router, including the primary route path and optionally the query parameters.
   * This function leverages reactivity to ensure that the returned value updates automatically whenever the router state changes.
   *
   * @param includeQuery - A boolean indicating whether query parameters should be included in the computed URL.
   * Defaults to `false`. If `true`, the query parameters are appended to the base path in the form of a query string.
   * @returns {Signal<string>} A reactive signal containing the computed URL as a string. The URL always starts with the base path
   * of the primary route, and may include query parameters when `includeQuery` is `true`.
   *
   * @example
   * ```typescript
   * const urlSignal = currentUrl(true);
   *
   * // Reactively log the current URL with query parameters
   * effect(() => {
   *   console.log(urlSignal());
   * });
   *
   * // Example output:
   * // "/dashboard?page=2&filter=active"
   * ```
   *
   * @remarks
   * - The base path is derived from the primary outlet's segments in the router's URL tree.
   * - Query parameters are encoded using `encodeURIComponent` to ensure proper formatting.
   * - If a query parameter has a `null` or empty string value, it is excluded from the query string.
   */
  readonly currentUrl = (includeQuery: boolean = false): Signal<string> =>
    computed(() => {
      this.urlSignal();
      const qp = this.params();
      const tree: UrlTree = this.router.parseUrl(this.router.url);
      const segments: string[] = tree.root.children['primary']?.segments?.map((s) => s.path) ?? [];
      const basePath = '/' + segments.filter(Boolean).join('/');

      if (!includeQuery) return basePath;

      const entries = Object.entries(qp).filter(([_, v]) => v != null && v !== '');
      const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
      return qs ? `${basePath}?${qs}` : basePath;
    });

  /**
   * Computes a reactive signal that returns the value associated with a given key from the current parameters object.
   * If the specified key does not exist, it returns `null`.
   *
   * @param {string} key - The key whose corresponding value is to be retrieved from the parameters object.
   * @returns {Signal<string | null>} A computed reactive signal containing the value associated with the given key
   * or `null` if the key is not present.
   *
   * @example
   * // Assuming `this.params` returns an object like { userId: '123', role: 'admin' }
   * const userSignal = param('userId');
   * console.log(userSignal()); // Outputs: '123'
   *
   * const nonExistentKeySignal = param('nonExistentKey');
   * console.log(nonExistentKeySignal()); // Outputs: null
   *
   * @remarks
   * - This function creates a computed signal, making it suitable for reactive use within a TypeScript application
   * leveraging a signals framework.
   * - The function leverages TypeScript’s `computed` utility for creating the signal.
   */
  readonly param = (key: string): Signal<string | null> => computed(() => (this.params()[key] as string) ?? null);

  /**
   * Merges the provided query parameters into the current set of pending updates.
   * Ensures that only valid key-value pairs are added and triggers a flush operation if not already scheduled.
   *
   * @param {QueryParamsInit} newParams - An object representing the new query parameters to merge.
   * Each key-value pair represents a parameter name and its corresponding value.
   * Nullish values (`null` or `undefined`) are ignored and not added.
   *
   * @returns {void} This method does not return any value but modifies the internal state of the instance.
   *
   * @example
   * ```typescript
   * const query = new QueryManager();
   * query.mergeParams({ search: 'typescript', page: 2 });
   *
   * // This will add `search=typescript` and `page=2` to the internal pending parameters
   * // and schedule a flush to apply these changes.
   * ```
   *
   * @remarks
   * Internally, this method converts all parameter values into strings before storing them.
   * If the flush process is already scheduled, it will not schedule another one.
   * Uses `queueMicrotask` to ensure the flush task runs as soon as possible after the current event loop.
   *
   * @see queueMicrotask
   */
  mergeParams(newParams: QueryParamsInit): void {
    for (const [k, v] of Object.entries(newParams)) {
      if (v == null) continue;
      this.pending.set(k, String(v));
    }
    if (!this.flushScheduled) {
      this.flushScheduled = true;
      queueMicrotask(() => {
        this.flushPending().catch((err: unknown) =>
          this.logs.error('UrlParamsService', 'Failed to flush pending query params', err)
        );
      });
    }
  }

  /**
   * Updates the current query parameters for the router navigation.
   *
   * This method processes the provided `newParams` object, filters out any parameters with null or undefined values,
   * and converts all remaining values to strings. It then triggers a navigation with the updated query parameters,
   * replacing the existing ones.
   *
   * @param {QueryParamsInit} newParams - An object representing the query parameters to be set.
   *                                      Properties with `null` or `undefined` values are ignored.
   * @returns {void} This method does not return a value.
   *
   * @example
   * ```typescript
   * const queryParams: QueryParamsInit = { page: 1, search: 'term', filter: undefined };
   * myComponent.setParams(queryParams);
   * // Navigates to the current route, replacing the existing query parameters with:
   * // { page: '1', search: 'term' }
   * ```
   *
   * @remarks
   * The method explicitly replaces the current query parameters, rather than merging them, when triggering
   * the navigation. Use caution if you expect certain query parameters to persist across updates.
   */
  setParams(newParams: QueryParamsInit): void {
    const qp: Record<string, string> = {};
    for (const [k, v] of Object.entries(newParams)) {
      if (v == null) continue;
      qp[k] = String(v);
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: qp,
      queryParamsHandling: '', // replace
      replaceUrl: true,
    });
  }

  /**
   * Removes specified parameters from the current set of parameters.
   * This method filters out any parameters whose keys match the provided keys,
   * while keeping the rest intact. It also ensures that parameters with null or empty string
   * values are excluded from the final set.
   *
   * @param {...string} keys - The list of parameter keys to be removed from the current parameters.
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Assume `this.params()` initially returns: { a: '1', b: '2', c: null, d: '' }
   * this.removeParams('b', 'c');
   * // After execution, the parameters will be updated to: { a: '1' }
   *
   * @remarks
   * The method modifies the internal parameter state by invoking `this.setParams()` with an updated parameter map.
   * Parameters with null or empty string values are filtered out regardless of whether their keys are provided in `keys`.
   *
   * @see {@link this.params} - Fetches the current parameters.
   * @see {@link this.setParams} - Updates the current parameters with the provided map.
   */
  removeParams(...keys: string[]): void {
    const updated: Record<string, string> = {};
    for (const [k, v] of Object.entries(this.params())) {
      if (!keys.includes(k) && v != null && v !== '') {
        updated[k] = String(v);
      }
    }
    this.setParams(updated);
  }

  /**
   * Clears all query parameters from the current route while maintaining the same path.
   *
   * This method navigates to the current route with an empty `queryParams` object and no query parameter handling,
   * effectively removing all query parameters from the URL. It relies on the Angular Router for navigation.
   *
   * @returns {void} This method does not return a value.
   *
   * @example
   * // Assuming the current URL is: '/dashboard?user=123&sort=asc'
   * componentInstance.clearParams();
   * // The resulting URL will be: '/dashboard'
   *
   * @remarks
   * - This method can be useful for resetting stateful query parameters in an Angular application.
   * - It utilizes `queryParamsHandling` set to an empty string to explicitly clear parameters, ensuring
   *   all query parameters are removed.
   * @see https://angular.io/api/router/Router#navigate
   */
  clearParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
      replaceUrl: true,
    });
  }

  /**
   * Flushes all pending query parameter updates by performing navigation with the accumulated changes.
   *
   * This method collects all pending query parameters from the internal pending state, clears the pending state,
   * and triggers a navigation event to update the route with the new query parameters. It ensures that changes
   * are not repeatedly scheduled and merges the query parameters with existing ones in the route.
   *
   * @returns {Promise<void>} A promise that resolves when the navigation with updated query parameters completes.
   *
   * @example
   * // Example usage:
   * // Assume `pending` contains some query parameters to be updated.
   * await instance.flushPending();
   * // After calling this, the pending queue is cleared, and the router navigates to update the URL
   * // with the new query parameters merged into the current route.
   *
   * @remarks
   * This method manipulates and updates the query parameters of the current route. It uses the router's
   * `navigate` method with the `queryParamsHandling: 'merge'` configuration to preserve existing query parameters.
   * Ensure the internal state of `pending` is manipulated appropriately before calling this method.
   */
  private async flushPending(): Promise<void> {
    this.flushScheduled = false;
    if (this.pending.size === 0) return;
    if (!this.isBrowser) {
      this.pending.clear();
      return;
    }
    const qp: Record<string, string> = {};
    for (const [k, v] of this.pending) qp[k] = v;
    this.pending.clear();
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: qp,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
